import DeviceSelect from "../../pages/protocols/devices/DeviceSelect";
import { useAuth } from "../../providers/authprovider";
import React, { createContext, useContext, useEffect, useId, useState } from "react";
import {
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Table,
  Alert,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import PickWorkerSelect from "../../pages/calendar/PickWorkerSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faPlus, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import { addressAsString } from "../AddressForm";
import { Typeahead } from "react-bootstrap-typeahead";
import CustomDialog from "../CustomDialog";
import { useMobileMode } from "../../utils/useMobile";
import { usePaginationContext } from "../../pages/protocols/ProtocolFormPagination";
import { AerationTable, CloneButton } from "./AerationTable";
import CustomTables from "./CustomTables";
import StairwellTables from "./StairwellTables";
import Schematics from "./Schematics";
import OptionalCheckbox from "./OptionalCheckbox";
import ConfirmDeleteElementDialog from "./ConfirmDeleteElementDialog";
import TableOffer from "./TableOffer";
import OfferTablePriceList from "./OfferTablePriceList";
import TableSelect from "./TableSelect";
import FansMeasurements from "./FansMeasurements";
import { FileUploader } from "./FileUploader";


const formContext = createContext("formContext");

export function useFormContext() {
  return useContext(formContext);
}

export default function ProvideFormContext({ value, children }) {
  return <formContext.Provider value={value}>{children}</formContext.Provider>;
}

export function BuildHtmlElement(props) {
  const { element } = props;

  switch (element.type) {
    case "text":
      return <Text value={element} />;
    case "textarea":
      return <TextArea value={element} />;
    case "date":
      return <DateInput value={element} />;
    case "radio":
      return <Radio value={element} />;
    case "logo_radio":
      return <LogoRadio value={element} />;
    case "switch":
      return <Switch value={element} />;
    case "worker":
      return <WorkerSelect value={element} />;
    case "table":
      return <FormTable value={element} />;
    case "table_offer":
      return <TableOffer value={element} />;
    case "table_price_list":
      return <OfferTablePriceList value={element} />;
    case "table_select":
      return <TableSelect value={element} />;
    case "fans_measurements":
      return <FansMeasurements value={element} />;
    case "ol":
      return <Ol value={element} />;
    case "ul":
      return <Ul value={element} />;
    case "section":
      return <Section value={element} />;
    case "optional_checkbox":
      return <OptionalCheckbox value={element} />;
    case "products_table":
      return <ProductsTable value={element} />;
    case "aeration_table":
      return <AerationTable value={element} />;
    case "devices":
      return <DeviceSelect value={element} />;
    case "clone_element":
      return <CloneButton value={element} />;
    case "custom_tables":
      return <CustomTables value={element} />;
    case "table_list":
      return <StairwellTables value={element} />;
    case "schematics":
      return <Schematics value={element} />;
    case "file_uploader":
      return <FileUploader value={element} />;
    default:
      return <HtmlTag value={element} />;
  }
}

export function WorkerSelect(props) {
  const element = props.value;
  const withTrainings =
    element.withTrainings === undefined ? true : element.withTrainings;
  const worker = element.value;
  const { user } = useAuth();
  const { onChange, workers } = useFormContext();
  const [disabled] = useState(() => {
    if (element.disabled) return true;

    return false;
  });
  const [workerType, setWorkerType] = useState(
    element.value?.workerType ?? "Serwisant"
  );

  const handlePickWorker = (id) => {
    const worker = workers.find((w) => w.id === id);

    if (worker)
      element.value = {
        id: worker.id,
        workerType: workerType,
        name: worker.name,
        trainings: worker.trainings,
        selected: worker.trainings.map((t) => t.id),
      };
    else element.value = { workerType: workerType };

    onChange(element);
  };

  const onChangeTrainingCheckbox = (event) => {
    const { value, checked } = event.target;

    const selected = worker.selected;

    if (checked) selected.push(value);
    else selected.splice(selected.indexOf(value), 1);

    element.value = { ...worker, selected: selected };

    onChange(element);
  };

  const handleSetWorkerType = (typeName) => {
    setWorkerType(typeName);
    element.value = { ...worker, workerType: typeName };
    onChange(element);
  };

  return (
    <div className={"p-2"}>
      <Row>
        <Col>
          <PickWorkerSelect
            workers={workers}
            onPick={handlePickWorker}
            onSetWorkerType={handleSetWorkerType}
            workerType={workerType}
            disabled={disabled}
            default={element.value?.id}
          />
        </Col>
        <Col>
          {worker &&
            withTrainings &&
            worker.trainings?.map((t) => {
              return (
                <div key={t.id}>
                  <Form.Check
                    type={"checkbox"}
                    label={t.name}
                    value={t.id}
                    checked={worker.selected.includes(t.id)}
                    onChange={onChangeTrainingCheckbox}
                  />
                </div>
              );
            })}
        </Col>
      </Row>
      <Row>{worker && <UserSignature worker={worker} />}</Row>
    </div>
  );
}

export function UserSignature({ worker }) {
  const {
    users: { getSignature },
  } = useAuth();
  const [data, setData] = useState({ url: undefined, id: worker?.id });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setError(null);
    setData({ url: undefined, id: worker.id });
    if (worker.id !== data.id || !data.url) {
      setLoading(true);
      getSignature(worker.id, (res) => {
        if (res.success) {
          setData({ url: URL.createObjectURL(res.content), id: worker.id });
        }

        if (res.error) {
          setError(res.error);
        }

        setLoading(false);
      });
    }
  }, [worker]);

  return (
    <div className="w-50">
      Podpis:
      {loading && <FontAwesomeIcon icon={faSpinner} spin />}
      {error && <Alert variant={"danger"}>{error}</Alert>}
      {data.url && (
        <img
          width={200}
          src={data.url}
          title={"Podpis pracownika"}
          alt={"Podpis pracownika dodany w sekcji pracownicy"}
        />
      )}
    </div>
  );
}

export function FormTable(props) {
  const element = props.value;
  const showLpCol = element.showLpCol === undefined ? true : element.showLpCol;
  const copyOnAdd = element.copyOnAdd === undefined ? false : element.copyOnAdd;
  const columns = element.columns;
  const values = element.value;
  const { onChange, onDelete, tableTextAsTextarea } = useFormContext();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const getRow = (index) => {
    let cols = [];
    const data = values[index] || columns;

    for (let x in data) {
      const column = data[x];

      if(column.type === "checkbox") {
        cols.push(
          <td className={"p-0"} key={x} width={column.defaultWidth}>
            <Form.Check 
              className="m-2"
              type="checkbox"
              checked={column.value}
              onChange={(event) => {
                data[x] = { ...column, value: event.target.checked };
                element.value[index] = data;
                onChange(element);
              }}
            />
          </td>
        );
      }
      else if (column.type === "select") {
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Select
              className={"border-0 bg-transparent"}
              defaultValue={column.value}
              onChange={(event) => {
                data[x] = { ...column, value: event.target.value };
                element.value[index] = data;
                onChange(element);
              }}
            >
              {column.options.map((opt) => {
                return (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                );
              })}
            </Form.Select>
          </td>
        );
      } else if (column.type === "autocomplete") {
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Control
              className="border-0 bg-transparent"
              list={`options-${x}`} // link input to datalist by id
              defaultValue={column.value}
              onChange={(event) => {
                data[x] = { ...column, value: event.target.value };
                element.value[index] = data;
                onChange(element);
              }}
            />
            <datalist id={`options-${x}`}>
              {column.options.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          </td>
        );
      } else if (column.type === "textarea") {
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Control
              className={"border-0 bg-transparent"}
              as={"textarea"}
              type={column.type}
              value={column.value}
              placeholder={column.label}
              onChange={(event) => {
                //element.value[index] = {...column, value: event.target.value}
                data[x] = { ...column, value: event.target.value };
                element.value[index] = data;
                onChange(element);
              }}
            />
          </td>
        );
      } else {
        const renderAsTextarea = tableTextAsTextarea && column.type === "text";
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Control
              className={`border-0 bg-transparent${renderAsTextarea ? " offer-table-cell-control" : ""}`}
              as={renderAsTextarea ? "textarea" : undefined}
              rows={renderAsTextarea ? 1 : undefined}
              type={column.type}
              value={column.value}
              placeholder={column.label}
              onChange={(event) => {
                //element.value[index] = {...column, value: event.target.value}
                data[x] = { ...column, value: event.target.value };
                element.value[index] = data;
                onChange(element);
              }}
            />
          </td>
        );
      }
    }

    const handleDeleteRow = () => {
      element.value.splice(index, 1);
      onChange({ ...element });
    };

    return (
      <tr key={index}>
        {showLpCol && <td>{index + 1}</td>}
        {cols}
        <td>
          <div className={"btn-close"} onClick={handleDeleteRow}></div>
        </td>
      </tr>
    );
  };

  const handleAddRow = () => {
    let rowDataToAdd;
    if (copyOnAdd) {
      //get latest row values if exists and copy it
      rowDataToAdd = values[values.length - 1] ?? columns;
    } else {
      rowDataToAdd = columns;
    }

    element.value.push([...rowDataToAdd]);
    onChange(element);
  };

  return (
    <div className={"m-2"}>
      <div className="d-flex align-items-center justify-content-between pb-2">
        <h5>{element.label}</h5>
        {element.deletable === true ? (
          <Button variant="outline-danger" size="sm" onClick={() => setShowDeleteDialog(true)}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        ) : null}
      </div>
      <ConfirmDeleteElementDialog
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => onDelete(element)}
      />
      <Table striped bordered hover>
        <thead>
          <tr>
            {showLpCol && <td>L.p.</td>}
            {columns.map((col, i) => {
              const labelInput = (
                <Form.Control
                  className={"border-0 bg-transparent"}
                  type="text"
                  value={col.label}
                  onChange={(event) => {
                    element.columns[i] = {
                      ...col,
                      label: event.target.value,
                    };
                    // data[x] = {...column, value: event.target.value}
                    // element.value[index] = data;
                    onChange(element);
                  }}
                />
              );

              return (
                <th key={i} className="p-0">
                  {col.hideInPdf !== undefined ? (
                    <InputGroup>
                      {labelInput}
                      <OverlayTrigger
                        placement={"top"}
                        overlay={
                          <Tooltip>
                            {col.hideInPdf ? "niewidoczne w PDF" : "widoczne w PDF"}
                          </Tooltip>
                        }
                      >
                        <InputGroup.Text className={"border-0 bg-transparent"}>
                          <Form.Check
                            type={"checkbox"}
                            className={"m-0"}
                            checked={!col.hideInPdf}
                            onChange={(event) => {
                              element.columns[i] = {
                                ...col,
                                hideInPdf: !event.target.checked,
                              };
                              onChange(element);
                            }}
                          />
                        </InputGroup.Text>
                      </OverlayTrigger>
                    </InputGroup>
                  ) : (
                    labelInput
                  )}
                </th>
              );
              //return <th key={i}>{col.label}</th>
            })}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {values.map((row, index) => {
            return getRow(index);
          })}
        </tbody>
      </Table>
      <Button variant={"outline-secondary"} onClick={handleAddRow}>
        <FontAwesomeIcon icon={faPlus} /> dodaj wiersz
      </Button>
    </div>
  );
}

export function Ol(props) {
  const element = props.value;
  const { onChange } = useFormContext();

  const handleAddRow = () => {
    element.value.push("");
    onChange(element);
  };

  const handleDeleteRow = (index) => {
    element.value.splice(index, 1);
    onChange(element);
  };

  return (
    <div className={"p-2"}>
      <h5>{element.label}</h5>
      {element.description && (
        <div className={"p-3"}>
          <Form.Control
            type={"text"}
            as={"textarea"}
            value={element.description}
            onChange={(e) => {
              element.description = e.target.value;
              onChange(element);
            }}
          />
        </div>
      )}
      <ol>
        {element.value.map((val, index) => {
          return (
            <li className={"m-1"} key={index}>
              <InputGroup className="mb-3">
                <Form.Control
                  type={"text"}
                  value={val}
                  onChange={(event) => {
                    element.value[index] = event.target.value;
                    onChange(element);
                  }}
                />
                <Button
                  variant={"outline-secondary"}
                  onClick={() => {
                    handleDeleteRow(index);
                  }}
                >
                  <FontAwesomeIcon icon={faClose} />
                </Button>
              </InputGroup>
            </li>
          );
        })}
      </ol>
      <Button variant={"outline-secondary"} onClick={handleAddRow}>
        <FontAwesomeIcon icon={faPlus} /> dodaj pozycję
      </Button>
    </div>
  );
}

export function Ul(props) {
  const element = props.value;
  const { registerPageGate, releasePageGate } = usePaginationContext();
  const gateId = useId();
  const isStandalone = useMobileMode();
  const controlFields = isStandalone && element.control;
  const { onChange } = useFormContext();

  const [checks, setChecks] = useState(element.value.map((v) => v.checked || false));


  useEffect(() => {
    setChecks(element.value.map((v) => v.checked || false));
  }, [element.value]);

  // ✅ Only trigger this after render when checks change.
  //    Gate the page ONLY when this Ul is an actual control list (controlFields).
  //    A non-control list (control=false) has no checkboxes, so its checks can
  //    never become all-true and would otherwise permanently block navigation.
  //    Each control list registers its own gate (unique gateId), so multiple
  //    control lists on one page no longer overwrite each other.
  useEffect(() => {
    if (!controlFields) {
      releasePageGate?.(gateId);
      return;
    }
    const allChecked = checks.length > 0 && checks.every(Boolean);
    registerPageGate?.(gateId, allChecked);
  }, [checks, controlFields, gateId, registerPageGate, releasePageGate]);

  // Release this list's gate when it unmounts (e.g. moving to another page).
  useEffect(() => {
    return () => releasePageGate?.(gateId);
  }, [gateId, releasePageGate]);

  const handleAddRow = () => {
    element.value.push({ label: "", description: "", checked: false });
    setChecks((prev) => [...prev, false]);
    onChange(element);
  };

  const handleDeleteRow = (index) => {
    element.value.splice(index, 1);
    setChecks((prev) => prev.filter((_, i) => i !== index));
    onChange(element);
  };

  const toggleCheck = (index) => {
    element.value[index].checked = !checks[index];
    setChecks((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
    onChange(element);
  };

  return (
    <div className={"p-2"}>
      <h5>{element.label}</h5>
      {element.description && (
        <div className={"p-3"}>
          <Form.Control
            type="text"
            as="textarea"
            value={element.description}
            onChange={(e) => {
              element.description = e.target.value;
              onChange(element);
            }}
          />
        </div>
      )}

      <ul
        style={{
          listStyle: controlFields ? "none" : "disc",
          paddingLeft: controlFields ? 0 : "1.25rem",
        }}
      >
        {element.value.map((val, index) => (
          <li key={index}>
            <Row className="align-items-start">
              {controlFields && (
                <Col xs="auto" className="pt-2">
                  <Form.Check
                    type="checkbox"
                    checked={checks[index]}
                    onChange={() => toggleCheck(index)}
                  />
                </Col>
              )}
              <Col>
                <Form.Control
                  type="text"
                  className="fw-bold border-0 mb-1"
                  value={val.label}
                  placeholder="wprowadź nazwę pozycji"
                  onChange={(event) => {
                    element.value[index] = {
                      ...element.value[index],
                      label: event.target.value,
                    };
                    onChange(element);
                  }}
                />
                <Form.Control
                  className="border-0 mb-1"
                  as="textarea"
                  rows={2}
                  value={val.description}
                  placeholder="dodaj opis"
                  onChange={(event) => {
                    element.value[index] = {
                      ...element.value[index],
                      description: event.target.value,
                    };
                    onChange(element);
                  }}
                />
              </Col>
              <Col xs="auto">
                <button
                  className="btn-close mt-2"
                  onClick={() => handleDeleteRow(index)}
                ></button>
              </Col>
            </Row>
          </li>
        ))}
      </ul>

      <Button variant="outline-secondary" onClick={handleAddRow}>
        <FontAwesomeIcon icon={faPlus} /> dodaj pozycję
      </Button>
    </div>
  );
}

export function Radio(props) {
  const element = props.value;
  const options = element.options;
  const { onChange } = useFormContext();

  const handleChange = (event) => {
    const checked = event.target.checked;
    const name = event.target.name;

    const copy = element.value.filter((v) => v !== name);

    if (checked) copy.push(name);

    element.value = copy;

    onChange(element);
  };

  const isChecked = (option) => {
    return element.value.indexOf(option) > -1;
  };

  return (
    <div className={`p-2 ${element.class}`}>
      <h5>{element.label}</h5>
      {options.map((option, key) => {
        return (
          <Form.Check
            key={key}
            type={"checkbox"}
            name={option}
            checked={isChecked(option)}
            onChange={handleChange}
            id={`${element.name}-${option}`}
            label={option}
          />
        );
      })}
    </div>
  );
}

export function Section(props) {
  const element = props.value;
  const { onRemoveSection, onChange } = useFormContext();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const pdfCheckboxId = useId();
  const children = [];

  if (!Array.isArray(element.children)) {
    children.push(element.children);
  } else {
    for (let x in element.children) {
      const child = element.children[x];
      children.push(<BuildHtmlElement key={x} element={child} />);
    }
  }

  const handleDeleteSection = () => {
    setShowConfirmDialog(true);
  };

  const hasHideInPdf = element.hideInPdf !== undefined;

  const handleChangeVisibleInPdf = (event) => {
    element.hideInPdf = !event.target.checked;
    onChange({ ...element });
  };

  return (
    <div className={element.class}>
      <CustomDialog
        show={showConfirmDialog}
        title="Usunięcie sekcji"
        content="Potwierdź usunięcie sekcji. Tej akcji nie można cofnąć."
        onClose={() => setShowConfirmDialog(false)}
        confirmButtonText="OK"
        onConfirm={() => {
          onRemoveSection(element);
        }}
        cancelButtonText="Anuluj"
      />
      <div className={"d-flex justify-content-between align-items-center gap-3"}>
        {hasHideInPdf && (
          <Form.Check
            type={"checkbox"}
            id={pdfCheckboxId}
            className={"m-0"}
            checked={!element.hideInPdf}
            onChange={handleChangeVisibleInPdf}
            label={element.hideInPdf ? "niewidoczne w PDF" : "widoczne w PDF"}
          />
        )}
        <Button className={"btn btn-close ms-auto"} onClick={handleDeleteSection} />
      </div>

      {children}
    </div>
  );
}

export function HtmlTag(props) {
  const element = props.value;
  const TAG = element.type;
  const { onDelete } = useFormContext();

  const children = [];

  if (!Array.isArray(element.children)) {
    children.push(element.children);
  } else {
    for (let x in element.children) {
      const child = element.children[x];
      children.push(<BuildHtmlElement key={x} element={child} />);
    }
  }

  if (TAG === "div" && element.deleteable === true) {
    children.unshift(
      <div key="delete" className="text-end">
        <Button className="btn btn-close" onClick={() => onDelete(element)} />
      </div>
    );
  }

  return React.createElement(TAG, { className: element.class }, children);
}

export function Text(props) {
  const { onChange } = useFormContext();

  const element = props.value;
  const handleChange = (event) => {
    element.value = event.target.value;

    onChange(element);
  };

  return (
    <div className={`${element.class}`}>
      <Form.Group className="mb-3 " controlId={element.name}>
        {element.label && <Form.Label>{element.label}</Form.Label>}

        <Form.Control
          type="text"
          placeholder=""
          className={element.class}
          value={element.value}
          onChange={handleChange}
        />
      </Form.Group>
    </div>
  );
}

export function BuildingSelect(props) {
  const { getBuildings } = useFormContext();
  const { building, onChange } = props;

  const defaultTypeheadValue = () => {
    if (building?.building_id === undefined) return "";

    let b = getBuildings.find((bu) => bu.id === building.building_id);

    if (b === undefined) return "";

    return b.name;
  };
  const handleSelectBuilding = (options) => {
    let id = options[0]?.id;
    let address = "";
    let building_name = "";

    const building = getBuildings.find((b) => b.id === id);

    if (building !== undefined) {
      address = addressAsString(building);
      building_name = building.name;
    }

    const value = {
      building_id: id,
      address: address,
      building_name: building_name,
    };

    onChange(value);
  };

  return (
    <div className={`m-2 `}>
      <Form.Group>
        <Form.Label>Obiekt</Form.Label>
        <Typeahead
          id="select_building_auto"
          labelKey="name"
          onChange={handleSelectBuilding}
          options={getBuildings}
          placeholder="Wybierz obiekt..."
          value={"shit"}
          defaultInputValue={defaultTypeheadValue()}
        />
      </Form.Group>
    </div>
  );
}

export function ProductsTable(props) {
  const element = props.value;
  const values = element.value;
  const totalPrice = element.total;
  const columns = element.columns;
  const { onChange, assortments } = useFormContext();
  const [filter, setFilter] = useState("");

  const handleAddRow = () => {
    element.value.push([
      ...columns.map((c) => {
        return { ...c };
      }),
    ]);
    onChange(element);
  };

  const getRow = (row, index) => {
    const handlePickProduct = (event) => {
      const assortment_name = event.target.value;

      const assortment = assortments?.find(
        (asr) => asr.name === assortment_name
      );

      if (assortment) row[1].value = Number(assortment.price).toFixed(2);

      row[0].value = assortment_name;

      element.value[index] = row;

      onChange(element);
    };

    const handleSetPrice = (event) => {
      row[1].value = event.target.value;

      element.value[index] = row;

      onChange(element);
    };

    const handleDeleteRow = () => {
      element.value.splice(index, 1);
      onChange({ ...element });
    };

    return (
      <tr key={index}>
        <td className={"text-center"}>{index + 1}</td>
        <td className={"p-0"}>
          <Form.Group>
            <Form.Control
              className={"border-0 bg-transparent"}
              list={"assortments"}
              value={row[0].value}
              id={"product_name"}
              onChange={handlePickProduct}
            />
          </Form.Group>
        </td>
        <td className={"p-0"}>
          <Form.Group>
            <Form.Control
              className={"border-0 bg-transparent"}
              type={"number"}
              min={0}
              step="0.01"
              pattern="^\d+(?:\.\d{1,2})?$"
              value={row[1].value}
              onChange={handleSetPrice}
            />
          </Form.Group>
        </td>
        <td>
          <div className={"btn-close"} onClick={handleDeleteRow}></div>
        </td>
      </tr>
    );
  };

  const onPickFilter = (event) => {
    setFilter(event.target.value);
  };

  const handleSetTotalPrice = (event) => {
    element.total = event.target.value;
    onChange(element);
  };

  return (
    <div className={"m-2"}>
      <h5>{element.label}</h5>
      <div className={"text-end"}>
        <InputGroup className="mb-3 w-25 float-end">
          <InputGroup.Text id="basic-addon1">Grupa: </InputGroup.Text>
          <Form.Select
            aria-label="Pick group to filter assortments"
            onChange={onPickFilter}
          >
            {element.groups.map((group) => {
              return (
                <option key={group.value} value={group.value}>
                  {group.name}
                </option>
              );
            })}
          </Form.Select>
        </InputGroup>
      </div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <td>L.p.</td>
            {columns.map((col, i) => {
              return <th key={i}>{col.label}</th>;
            })}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {values.map((row, index) => {
            return getRow(row, index);
          })}
          <tr>
            <td></td>
            <td>Łącznie:</td>
            <td>
              <Form.Control
                as={"textarea"}
                className={"border-0 bg-transparent"}
                placeholder={values
                  .reduce((acc, row) => {
                    return acc + Number(row[1].value);
                  }, 0)
                  .toFixed(2)}
                value={totalPrice}
                onChange={handleSetTotalPrice}
              />
            </td>
            <td></td>
          </tr>
        </tbody>
      </Table>
      <datalist id="assortments">
        {assortments
          ?.filter((a) => {
            if (!filter || filter.length <= 0) return true;

            return a.group === filter;
          })
          .map((a) => {
            return (
              <option key={a.id} value={`${a.name}`}>
                cena: {a.price}
              </option>
            );
          })}
      </datalist>
      <Button variant={"outline-secondary"} onClick={handleAddRow}>
        <FontAwesomeIcon icon={faPlus} /> dodaj wiersz
      </Button>
    </div>
  );
}

export function TextArea(props) {
  const element = props.value;
  const { onChange } = useFormContext();

  const handleChange = (event) => {
    element.value = event.target.value;

    onChange(element);
  };

  return (
    <div className={`m-2 ${element.class}`}>
      <Form.Group className="mb-3" controlId={element.name}>
        <Form.Label>{element.label}</Form.Label>
        <Form.Control
          as="textarea"
          rows={element.lines}
          value={element.value}
          onChange={handleChange}
        />
      </Form.Group>
    </div>
  );
}

export function DateInput(props) {
  const element = props.value;
  const { onChange } = useFormContext();
  const handleChange = ({ target }) => {
    element.value = target.value;

    if (props.onChange) return props.onChange(element.value);

    onChange(element);
  };

  return (
    <div className={`m-2 ${element.class}`}>
      <Form.Group className="mb-3" controlId={element.name}>
        <Form.Label>{element.label}</Form.Label>
        <Form.Control
          type={"date"}
          value={element.value}
          onChange={handleChange}
        />
      </Form.Group>
    </div>
  );
}

export function LogoRadio(props) {
  const element = props.value;
  const options = element.options;

  const { onChange } = useFormContext();

  const handleChange = (event) => {
    const checked = event.target.checked;
    const value = event.target.value;

    const copy = element.value.filter((v) => v !== value);

    if (checked) copy.push(value);

    element.value = copy;

    onChange(element);
  };

  const isChecked = (option) => {
    return element.value.indexOf(option.id) > -1;
  };

  return (
    <div className={`p-2 ${element.class}`}>
      <h5>{element.label}</h5>
      {options.map((option, key) => {
        return (
          <div key={key} className="d-flex align-items-center mb-2">
            <Form.Check
              type={"checkbox"}
              name={option.label}
              checked={isChecked(option)}
              value={option.id}
              onChange={handleChange}
              id={`${element.name}-${option.id}`}
              label={option.label}
              className="mr-2"
            />
            {/* Add the image here */}
            <img
              src={"/" + option.url} // Image URL from the options array
              alt={option.label} // Alt text for the image
              className="ms-2"
              style={{ width: "200px" }} // Adjust the size as needed
            />
          </div>
        );
      })}
    </div>
  );
}

export function Switch(props) {
  const element = props.value;
  const options = element.options;
  const { onChange } = useFormContext();

  const handleChange = (event) => {
    const checked = event.target.checked;
    const name = event.target.value;

    element.value = name;

    onChange(element);
  };

  return (
    <div className={`p-2 ${element.class}`}>
      <h5>{element.label}</h5>
      <div className={"d-flex"}>
        {options.map((option, key) => {
          return (
            <Form.Check
              className={"m-2"}
              key={key}
              type={"radio"}
              name={element.name}
              value={option}
              checked={element.value === option}
              onChange={handleChange}
              id={`${element.name}-${option}`}
              label={option}
            />
          );
        })}
      </div>
    </div>
  );
}
